import {
	ChangeDetectionStrategy,
	Component,
	effect,
	inject,
	signal,
	computed,
} from '@angular/core';
import { AddressFormatDropdownOptions } from '../finances/business-finances-general/business-finances-general.types';
import { AddressFormat } from '@models/businessSettings/businessFormatTypes';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { BusinessDispatch, BusinessState } from '@state/business';
import { Store } from '@ngxs/store';
import { AddressFormattingSettings } from '@models/businessSettings/settings';
import { addressBlock, genericAddress } from '@core/util/address';
import { distinctUntilChanged } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
	selector: 'app-address-formatting',
	templateUrl: './address-formatting.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class AddressFormattingComponent {
	public addressFormatDropdownOptions = AddressFormatDropdownOptions;

	addressFormattingSettings = toSignal(
		inject(Store)
			.select<AddressFormattingSettings>(
				BusinessState.getAddressFormattingSettings,
			)
			.pipe(
				distinctUntilChanged(
					(prev, curr) =>
						prev?.format === curr?.format &&
						prev?.hasComma === curr?.hasComma &&
						prev?.addSpace === curr?.addSpace &&
						prev?.provinceLongForm === curr?.provinceLongForm,
				),
			),
	);
	businessDispatch = inject(BusinessDispatch);

	address = signal<string[]>([]);
	dropdownText = computed(
		() =>
			this.getFormatDisplayName(this.addressFormattingSettings()?.format) ??
			'Format 1', // Fallback to 'Format 1'
	);

	constructor() {
		effect(
			() => {
				this.address.set(
					addressBlock(genericAddress, this.addressFormattingSettings()).split(
						'<br>',
					),
				);
			},
			{ allowSignalWrites: true },
		);
	}

	setSelectedAddressFormat(
		$event: DropdownResult<DropdownOption<AddressFormat>>,
	): void {
		this.businessDispatch.UpdateDefaultSettings({
			addressFormatting: {
				format: $event.result.value,
			},
		});
	}

	setAddSpace(value: boolean) {
		this.toggleSetting(value, 'addSpace');
	}

	setHasComma(value: boolean): void {
		this.toggleSetting(value, 'hasComma');
	}

	setProvinceLongForm(value: boolean): void {
		this.toggleSetting(value, 'provinceLongForm');
	}

	private toggleSetting(
		value: boolean,
		field: 'addSpace' | 'hasComma' | 'provinceLongForm',
	) {
		this.businessDispatch.OverwriteBusinessFields(
			`defaultSettings.addressFormatting.${field}`,
			value,
		);
	}

	getFormatDisplayName(addressFormat: AddressFormat): string {
		return this.addressFormatDropdownOptions.find(
			(option) => option.value === addressFormat,
		)?.name;
	}
}
